import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { NotificationTypes } from 'src/app/app.enums';
import { FilterByPlatformPipe } from 'src/app/pipes/filter-by-platform.pipe';
import { AuthConfig, AuthService } from 'src/app/Services/auth.service';
import { ConsignmentService, GalleryConsignmentProduct } from 'src/app/Services/consignment.service';
import { NotificationsService } from 'src/app/Services/shared/notifications.service';
import { ShopifyService } from 'src/app/Services/shopify-service/shopify.service';
import { SquareSpaceService } from 'src/app/Services/square-space/square-space.service';
import { SquareupPosService } from 'src/app/Services/squareup-pos.service.ts/squareup-pos.service';
import { WoocomerceService } from 'src/app/Services/woocomerce-service/woocomerce.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'app-sync-products',
    templateUrl: './sync-products.component.html',
    providers: [FilterByPlatformPipe],
    standalone: false
})
export class SyncProductsComponent implements OnInit {
  // Input bindings from parent
  @Input() SyncProducts: GalleryConsignmentProduct[] = [];
  @Input() SquareupSyncProducts = false;
  @Input() shopifySyncProducts = false;
  @Input() wooCommerceSyncProducts = false;
  @Input() SquareSpaceSyncProducts = false;

  // Public state
  public authConfig!: AuthConfig;
  public filteredProducts: GalleryConsignmentProduct[] = [];
  public DisplayRemoveProductDialog = false;
  public selectedProduct: GalleryConsignmentProduct | null = null;
  public galleryProductsLoaded = false;
  public selectedStoreName = '';
  public storeFilterOptions: any[] = [];
  public selectedStoreFilter: any = null;
  public showStoreFilter = false;
  public activePlatform = '';
  // Internal tracking for original data
  private allSyncProducts: GalleryConsignmentProduct[] = [];

  constructor(
    private consignmentService: ConsignmentService,
    private router: Router,
    private WoocomerceService: WoocomerceService,
    private ShopifyService: ShopifyService,
    private SquareupPosService: SquareupPosService,
    private ngxSpinnerService: NgxSpinnerService,
    private notificationsService: NotificationsService,
    private authService: AuthService,
    private squareSpaceService: SquareSpaceService,
    private transloco: TranslocoService
  ) {}

  ngOnInit(): void {
    this.authConfig = this.authService.getAuthConfig();
    this.loadProductsForActivePlatform();
  }

  // Reload specific platform manually
  public reloadPlatformProducts(platform: string): void {
    switch (platform) {
      case 'SquareSpace': this.getSquareSpaceSynProducts(); break;
      case 'WooCommerce': this.getWooCommerceSyncProducts(); break;
      case 'Shopify': this.getShopifySyncProducts(); break;
    }
  }

  // Determine initial platform and load its products
  private loadProductsForActivePlatform(): void {
    if (this.wooCommerceSyncProducts) {
      this.activePlatform = 'WooCommerce';
      this.getWooCommerceSyncProducts();
    } else if (this.shopifySyncProducts) {
      this.activePlatform = 'Shopify';
      this.getShopifySyncProducts();
    } else if (this.SquareupSyncProducts) {
      this.activePlatform = 'Squareup';
      this.getSquareUpSyncProducts();
    } else if (this.SquareSpaceSyncProducts) {
      this.activePlatform = 'SquareSpace';
      this.getSquareSpaceSynProducts();
    }
  }

  // Common handler for setting and filtering platform products
  private handleProductResponse(response: GalleryConsignmentProduct[], platform: string): void {
    this.SyncProducts = response.filter(product =>
      product.ProductSyncedOnPlatformDTO?.some(p => p.PlatformName === platform)
    );
    this.allSyncProducts = [...this.SyncProducts];
    this.galleryProductsLoaded = true;
    this.initializeStoreFilterOptions();
    this.filterProductsByStore();
  }

  // Platform-specific product fetch methods
  public getWooCommerceSyncProducts(): void {
    this.WoocomerceService.getWooCommerceSyncProducts().subscribe(response => {
      this.handleProductResponse(response, 'WooCommerce');
    });
  }

  public getSquareSpaceSynProducts(): void {
    this.squareSpaceService.getSquareupSyncProducts().subscribe(response => {
      this.handleProductResponse(response, 'SquareSpace');
    });
  }

  public getSquareUpSyncProducts(): void {
    this.SquareupPosService.getSquareupSyncProducts().subscribe(response => {
      this.handleProductResponse(response, 'SquareSpace');
    });
  }

  public getShopifySyncProducts(): void {
    this.ShopifyService.getShopifySyncProducts().subscribe(response => {
      this.handleProductResponse(response, 'Shopify');
    });
  }

  // Navigate to artwork details page
  public showProductDetails(product: GalleryConsignmentProduct): void {
    const url = this.authConfig.IsArtist
      ? ['/', product.Product.ArtistId, 'artworks', product.Product.Id]
      : [product.ArtistId, 'artworks', product.Product.Id];
    this.router.navigate(url);
  }

  // Show or hide product removal confirmation dialog
  public openRemoveProductDialog(product: GalleryConsignmentProduct): void {
    this.selectedProduct = product;
    this.DisplayRemoveProductDialog = true;
  }

  public closeDialog(): void {
    this.DisplayRemoveProductDialog = false;
    this.selectedProduct = null;
  }

  // Trigger product removal based on active platforms
  public removeProduct(storeId?: string): void {
    if (!this.selectedProduct) {
      this.notificationsService.showNotification(this.transloco.translate('syncProducts.warningTitle'), this.transloco.translate('syncProducts.noSelectedProduct'), NotificationTypes.Error);
      return;
    }

    this.getActivePlatforms().forEach(platform => {
      switch (platform) {
        case 'WooCommerce': this.removeSyncedProductsFromWoo(this.selectedProduct!, storeId); break;
        case 'Shopify': this.removeSyncedProductsFromShopify(this.selectedProduct!, storeId); break;
        case 'SquareSpace': this.removeSyncedProductFromSquareSpace(this.selectedProduct!, storeId); break;
        default: this.removeProductFromPlatform(platform, storeId); break;
      }
    });
  }

  // Handle success message for removal
  private handleRemoveResponse(response: any, platform: string): void {
    this.ngxSpinnerService.hide();
    const res = response.ResponseObject ?? response;
    const message = res.ResponseMessage?.trim()?.length
      ? res.ResponseMessage
      : res.Success ? this.transloco.translate('syncProducts.removedSuccess', { platform }) : this.transloco.translate('syncProducts.failedToRemoveProduct', { platform });
    const type = res.Success ? NotificationTypes.Success : NotificationTypes.Error;

    this.notificationsService.showNotification(res.Success ? this.transloco.translate('syncProducts.successTitle') : this.transloco.translate('syncProducts.errorTitle'), message, type);

    if (res.Success) {
      this.loadProductsForActivePlatform();
      this.DisplayRemoveProductDialog = false;
    }
  }

  // Handle failed response
  private handleRemoveError(error: any, platform: string): void {
    this.ngxSpinnerService.hide();
    const message = error?.error?.ResponseMessage || this.transloco.translate('syncProducts.noErrorMessageFromServer', { platform });
    this.notificationsService.showNotification(this.transloco.translate('syncProducts.errorTitle'), message, NotificationTypes.Error);
  }

  // WooCommerce product removal
  private removeSyncedProductsFromWoo(product: GalleryConsignmentProduct, storeId?: string): void {
    const ids = (product.ProductSyncedOnPlatformDTO ?? [])
      .filter(p => p.PlatformName === 'WooCommerce' && (!storeId || p.WooCommerceStoreDetail?.Id === storeId))
      .map(p => p.Id);

    if (!ids.length) {
      this.notificationsService.showNotification(this.transloco.translate('syncProducts.warningTitle'), this.transloco.translate('syncProducts.noWooCommerceProductIds'), NotificationTypes.Error);
      return;
    }

    this.ngxSpinnerService.show();
    this.WoocomerceService.removeSyncdProducts(ids).subscribe(
      res => this.handleRemoveResponse(res, 'WooCommerce'),
      err => this.handleRemoveError(err, 'WooCommerce')
    );
  }

  // Shopify product removal
  private removeSyncedProductsFromShopify(product: GalleryConsignmentProduct, storeId?: string): void {
    const ids = (product.ProductSyncedOnPlatformDTO ?? [])
      .filter(p => p.PlatformName === 'Shopify' && (!storeId || p.ShopifyStoreDetailDTO?.Id === storeId))
      .map(p => p.Id);

    if (!ids.length) {
      this.notificationsService.showNotification(this.transloco.translate('syncProducts.warningTitle'), this.transloco.translate('syncProducts.noShopifyProductIds'), NotificationTypes.Error);
      return;
    }

    this.ngxSpinnerService.show();
    this.ShopifyService.removeSyncedProductById(ids).subscribe(
      res => this.handleRemoveResponse(res, 'Shopify'),
      err => this.handleRemoveError(err, 'Shopify')
    );
  }

  // SquareSpace product removal
  private removeSyncedProductFromSquareSpace(product: GalleryConsignmentProduct, siteId?: string): void {
    this.ngxSpinnerService.show();
    const productId = product.ProductSyncedOnPlatformDTO.find(
      p => p.PlatformName === 'SquareSpace' && p.SquareSpaceSitesDetailDto?.Id === siteId
    )?.Id;
    const ids = productId ? [productId] : [];

    if (!ids.length) {
      this.ngxSpinnerService.hide();
      this.notificationsService.showNotification(this.transloco.translate('syncProducts.warningTitle'), this.transloco.translate('syncProducts.noSquareSpaceProductIds'), NotificationTypes.Error);
      return;
    }

    this.squareSpaceService.deleteSquareSpaceSyncProduct(ids).subscribe(
      res => this.handleRemoveResponse(res, 'SquareSpace'),
      err => this.handleRemoveError(err, 'SquareSpace')
    );
  }

  // For future platforms if needed
  private removeProductFromPlatform(platform: string, storeId?: string): void {
    const accounts = (this.selectedProduct!.ProductSyncedOnPlatformDTO ?? [])
      .filter(p => p.PlatformName === platform && (p.WooCommerceStoreDetail?.Id || p.ShopifyStoreDetailDTO?.Id))
      .map(p => p.WooCommerceStoreDetail?.Id || p.ShopifyStoreDetailDTO?.Id);

    const platformAccountIds = storeId ? [storeId] : accounts;

    if (!platformAccountIds.length) {
      this.notificationsService.showNotification(this.transloco.translate('syncProducts.warningTitle'), this.transloco.translate('syncProducts.noPlatformAccountIds', { platform }), NotificationTypes.Error);
      return;
    }

    this.ngxSpinnerService.show();
    this.consignmentService.removeSyncedProductToIntegratedPlatforms(
      this.selectedProduct!.ConsignmentId,
      this.selectedProduct!.ConsignmentProductId,
      this.selectedProduct!.Product.Id,
      platform,
      platformAccountIds
    ).subscribe({
      next: res => this.handleRemoveResponse(res, platform),
      error: err => this.handleRemoveError(err, platform)
    });
  }

  // Determine which platforms are active
  private getActivePlatforms(): string[] {
    return [
      this.wooCommerceSyncProducts && 'WooCommerce',
      this.shopifySyncProducts && 'Shopify',
      this.SquareupSyncProducts && 'Squareup',
      this.SquareSpaceSyncProducts && 'SquareSpace'
    ].filter(Boolean) as string[];
  }

  // Build store dropdown filter
 private initializeStoreFilterOptions(): void {
  const options = this.SyncProducts
    .flatMap(product => product.ProductSyncedOnPlatformDTO ?? [])
    .filter(p => p.PlatformName === this.activePlatform)
    .map(p => {
      const label = p.WooCommerceStoreDetail?.StoreName || 
                   p.ShopifyStoreDetailDTO?.StoreName || 
                   p.SquareSpaceSitesDetailDto?.SiteInfo?.title;
      const value = p.WooCommerceStoreDetail?.Id || 
                   p.ShopifyStoreDetailDTO?.Id || 
                   p.SquareSpaceSitesDetailDto?.Id;
      
      // Only include if both label and value are truthy
      return label && value ? { label, value } : null;
    })
    .filter(option => option !== null); // Remove null options

  this.storeFilterOptions = Array.from(new Map(options.map(o => [o!.value, o])).values());
  this.showStoreFilter = this.storeFilterOptions.length > 0;
}

  // Filter products by selected store ID
  public filterProductsByStore(): void {
    if (!this.selectedStoreFilter) {
      this.SyncProducts = [...this.allSyncProducts];
      return;
    }

    this.SyncProducts = this.allSyncProducts.filter(product =>
      product.ProductSyncedOnPlatformDTO?.some(platform => (
        (this.wooCommerceSyncProducts && platform.PlatformName === 'WooCommerce' && platform.WooCommerceStoreDetail?.Id === this.selectedStoreFilter) ||
        (this.shopifySyncProducts && platform.PlatformName === 'Shopify' && platform.ShopifyStoreDetailDTO?.Id === this.selectedStoreFilter) ||
        (this.SquareSpaceSyncProducts && platform.PlatformName === 'SquareSpace' && platform.SquareSpaceSitesDetailDto?.Id === this.selectedStoreFilter)
      ))
    );
  }

  // When user selects a store from dropdown
  public onStoreFilterChange(): void {
    const selected = this.storeFilterOptions.find(opt => opt.value === this.selectedStoreFilter);
    this.selectedStoreName = selected ? selected.label : '';
    this.filterProductsByStore();
  }
}
